var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback } from 'react';
import { Loader } from '@ui';
import { Text } from '@ui/atoms/Text';
import { translate } from '@utils/i18n';
import { useCalendarSettings } from './hooks/useCalendarSettings';
import { useUpdateCalendarSettings } from './hooks/useUpdateCalendarSettings';
import { OptimizationToggle } from './components/OptimizationToggle';
import { CalendarVisitLimits } from './components/CalendarVisitLimits';
import { SeriesStartHour } from './components/SeriesStartHour';
import { DEFAULT_CALENDAR_MONTH_VISIT_LIMIT, DEFAULT_CALENDAR_WEEK_VISIT_LIMIT, DEFAULT_CALENDAR_DAY_MAX_EVENTS, DEFAULT_SERIES_START_HOUR } from './types';
import './SettingsStyles.scss';
const namespace = 'features.settings.calendar_settings';
function Settings({ calendarOptimizationEnabled: initialEnabled, calendarMonthVisitLimit: initialMonthLimit = DEFAULT_CALENDAR_MONTH_VISIT_LIMIT, calendarWeekVisitLimit: initialWeekLimit = DEFAULT_CALENDAR_WEEK_VISIT_LIMIT, calendarDayMaxEvents: initialDayMaxEvents = DEFAULT_CALENDAR_DAY_MAX_EVENTS, inspectionSeriesDefaultStartHour: initialStartHour = DEFAULT_SERIES_START_HOUR }) {
    var _a, _b, _c, _d, _e;
    const { data: settings, isLoading } = useCalendarSettings();
    const { mutateAsync, isLoading: isMutationLoading } = useUpdateCalendarSettings();
    const enabled = (_a = settings === null || settings === void 0 ? void 0 : settings.calendarOptimizationEnabled) !== null && _a !== void 0 ? _a : initialEnabled;
    const savedMonthLimit = (_b = settings === null || settings === void 0 ? void 0 : settings.calendarMonthVisitLimit) !== null && _b !== void 0 ? _b : initialMonthLimit;
    const savedWeekLimit = (_c = settings === null || settings === void 0 ? void 0 : settings.calendarWeekVisitLimit) !== null && _c !== void 0 ? _c : initialWeekLimit;
    const savedDayMaxEvents = (_d = settings === null || settings === void 0 ? void 0 : settings.calendarDayMaxEvents) !== null && _d !== void 0 ? _d : initialDayMaxEvents;
    const savedStartHour = (_e = settings === null || settings === void 0 ? void 0 : settings.inspectionSeriesDefaultStartHour) !== null && _e !== void 0 ? _e : initialStartHour;
    const handleToggle = () => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        void mutateAsync({ calendar_optimization_enabled: !enabled });
    };
    const handleCommitLimits = useCallback((updates) => __awaiter(this, void 0, void 0, function* () {
        const payload = {};
        if (updates.month !== undefined) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payload.calendar_month_visit_limit = updates.month;
        }
        if (updates.week !== undefined) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payload.calendar_week_visit_limit = updates.week;
        }
        if (updates.dayMax !== undefined) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payload.calendar_day_max_events = updates.dayMax;
        }
        if (Object.keys(payload).length > 0) {
            yield mutateAsync(payload);
        }
    }), [mutateAsync]);
    const handleCommitStartHour = useCallback((hour) => __awaiter(this, void 0, void 0, function* () {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        yield mutateAsync({ inspection_series_default_start_hour: hour });
    }), [mutateAsync]);
    const pageTitle = (React.createElement(Text, { variant: "x-large", as: "h1" }, translate('title', { namespace })));
    if (isLoading) {
        return (React.createElement("div", { className: "workspace__major--form" },
            React.createElement("header", { className: "workspace__header--alt--sticky" },
                React.createElement("div", { className: "header__main" }, pageTitle)),
            React.createElement("div", { className: "calendar-settings__loading" },
                React.createElement(Loader, { size: "48" }))));
    }
    return (React.createElement("div", { className: "workspace__major--form" },
        React.createElement("header", { className: "workspace__header--alt--sticky" },
            React.createElement("div", { className: "header__main" }, pageTitle)),
        React.createElement("section", { className: "calendar-settings-container" },
            React.createElement(OptimizationToggle, { enabled: enabled, isUpdating: isMutationLoading, onToggle: handleToggle }),
            React.createElement(CalendarVisitLimits, { optimizationEnabled: enabled, isUpdating: isMutationLoading, savedMonthLimit: savedMonthLimit, savedWeekLimit: savedWeekLimit, savedDayMaxEvents: savedDayMaxEvents, onCommitLimits: handleCommitLimits }),
            React.createElement(SeriesStartHour, { isUpdating: isMutationLoading, savedStartHour: savedStartHour, onCommitStartHour: handleCommitStartHour }))));
}
export default Settings;
